import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency, formatNumber, formatPercent, formatRoas } from "@/lib/formatters";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

type MetricKey = "spend" | "impressions" | "clicks" | "roas";
const TOGGLES: { key: MetricKey; label: string; fmt: (v: number) => string }[] = [
  { key: "spend", label: "Spend", fmt: formatCurrency },
  { key: "impressions", label: "Impr.", fmt: formatNumber },
  { key: "clicks", label: "Clicks", fmt: formatNumber },
  { key: "roas", label: "ROAS", fmt: formatRoas },
];

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function PlacementBreakdownChart({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const [metric, setMetric] = useState<MetricKey>("spend");
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard-breakdown-placement", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("breakdown/placement", params));
      if (!res.ok) throw new Error("Failed to fetch data");
      return res.json();
    },
  });

  const fmtFn = TOGGLES.find(t => t.key === metric)!.fmt;
  const empty = !isLoading && !error && (!data || data.length === 0);

  return (
    <DashboardWidget title="Placement Breakdown" widgetType="chart" isEditMode={isEditMode} isLoading={isLoading} error={error?.message ?? null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#8C8578" }} data-testid="empty-placement">No placement data available</div>
      ) : (
        <div className="px-4 pb-3 h-full flex flex-col" style={{ background: "#F8F8F7" }} data-testid="chart-placement">
          <div className="flex gap-1 mb-2 pt-2">
            {TOGGLES.map(t => (
              <button
                key={t.key}
                onClick={() => setMetric(t.key)}
                className={`px-2 py-0.5 text-[10px] font-medium rounded-full border transition-colors ${
                  metric === t.key ? "text-white border-transparent" : "hover:opacity-70"
                }`}
                style={metric === t.key ? { background: "#6366F1" } : { borderColor: "#E5E5E3", color: "#8C8578" }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data || []} layout="vertical" margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#8C8578" }} tickFormatter={(v: number) => fmtFn(v)} />
                <YAxis type="category" dataKey="placement" tick={{ fontSize: 10, fill: "#8C8578" }} width={80} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E5E5E3" }} formatter={(v: number) => [fmtFn(v), TOGGLES.find(t => t.key === metric)?.label]} />
                <Bar dataKey={metric} fill="#6366F1" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </DashboardWidget>
  );
}
